import { forwardRef, useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
  /**
   * Option visible mais non sélectionnable. Préférable à la retirer de la
   * liste : l'utilisateur voit que l'élément existe et POURQUOI il ne peut
   * pas le choisir (cf `hint`), au lieu de le chercher en vain.
   */
  disabled?: boolean;
  /** Précision affichée sous le libellé — typiquement la raison d'un `disabled`. */
  hint?: string;
}

export interface MultiSelectProps {
  value: string[];
  options: MultiSelectOption[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
}

const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  ({ value, options, onChange, placeholder = 'Selectionnez...', className }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const handleToggleOption = (option: MultiSelectOption) => {
      const optionValue = option.value;
      // Une option désactivée reste retirable si elle était déjà sélectionnée
      // (état antérieur à sa désactivation) : on ne bloque que l'ajout.
      if (option.disabled && !value.includes(optionValue)) return;
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    };

    const handleRemoveTag = (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(value.filter((v) => v !== optionValue));
    };

    const selectedOptions = options.filter((opt) => value.includes(opt.value));

    // IMPORTANT : le ref `containerRef` est attaché au wrapper extérieur,
    // pas au dropdown intérieur. Ainsi `handleClickOutside` mesure bien
    // "clic en dehors du composant entier" (zone visible + dropdown).
    // Le dropdown est rendu en SIBLING (et non child) de la zone clickable
    // qui toggle `isOpen`, sinon tout clic dans le dropdown bubble vers le
    // toggle et ferme le menu instantanement.
    return (
      <div ref={ref || containerRef} className={cn('relative', className)}>
        {/* Zone visible (tags + chevron) — cliquable pour ouvrir/fermer */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
          className={cn(
            'flex min-h-[40px] w-full cursor-pointer flex-wrap items-center gap-1 rounded-lg border bg-white px-3 py-1.5 text-sm transition-colors',
            'hover:border-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isOpen ? 'border-primary-500 ring-2 ring-primary-500' : 'border-neutral-300'
          )}
        >
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700"
              >
                {option.label}
                <button
                  type="button"
                  onClick={(e) => handleRemoveTag(option.value, e)}
                  className="rounded hover:bg-primary-200 focus-visible:outline-none"
                  aria-label={`Retirer ${option.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          ) : (
            <span className="text-neutral-400">{placeholder}</span>
          )}
          <ChevronDown
            className={cn(
              'ml-auto h-4 w-4 text-neutral-500 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>

        {/* Dropdown — sibling (pas child) du toggle pour eviter que le click
            sur une option bubble vers le toggle et ferme le menu. */}
        {isOpen && (
          <div
            className={cn(
              'absolute left-0 top-full z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-lg',
              'animate-in fade-in-0 zoom-in-95'
            )}
          >
            {options.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => handleToggleOption(option)}
                  disabled={option.disabled && !isSelected}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-neutral-100 focus-visible:bg-neutral-100 focus-visible:outline-none',
                    'disabled:cursor-not-allowed disabled:hover:bg-transparent',
                    isSelected && 'bg-primary-50'
                  )}
                >
                  <span className="flex min-w-0 flex-col">
                    <span
                      className={cn(
                        isSelected ? 'text-primary-700' : 'text-dark-500',
                        option.disabled && !isSelected && 'text-neutral-400'
                      )}
                    >
                      {option.label}
                    </span>
                    {option.hint && <span className="text-xs text-neutral-500">{option.hint}</span>}
                  </span>
                  {isSelected && <Check className="h-4 w-4 text-primary-500" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
